import React, { useMemo, useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material"
import { DataGridPro, GridSelectionModel } from "@mui/x-data-grid-pro"
import { formatUtcToLocal } from "../../helper/utils"

export interface MatchedRemovedItem {
  importRowIndex?: number
  customerPolicyId: number
  policyNumber?: string
  invoiceNumber?: string
  stateId?: number
  client?: string
  transactionTypeId?: number
  transactionType?: string
  taxType?: string
  taxAmt?: string
  grossPremium?: string
  invDate?: string
  effectiveDate?: string
  expirationDate?: string
}

interface OTRRemovedQueueRestoreDialogProps {
  open: boolean
  onClose: () => void
  matchedItems: MatchedRemovedItem[]
  matchedCustomerPolicyIds: number[]
  totalImportCount: number
  onRestoreSelected: (customerPolicyIds: number[]) => Promise<void>
  onRestoreAll: () => Promise<void>
  onDontRestore: () => void
  loading?: boolean
}

const PAGE_SIZE = 25

export function OTRRemovedQueueRestoreDialog({
  open,
  onClose,
  matchedItems,
  matchedCustomerPolicyIds,
  totalImportCount,
  onRestoreSelected,
  onRestoreAll,
  onDontRestore,
  loading = false,
}: OTRRemovedQueueRestoreDialogProps) {
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])
  const [actionLoading, setActionLoading] = useState(false)

  const rows = useMemo(() => {
    const fmt = (v: string | null | undefined) =>
      v != null && String(v).trim() !== ""
        ? formatUtcToLocal(v, "MM/DD/YYYY", true)
        : ""
    return matchedItems.map((item, index) => ({
      id: `row-${item.customerPolicyId}-${index}`,
      _customerPolicyId: item.customerPolicyId,
      policyNumber: item.policyNumber ?? "",
      invoiceNumber: item.invoiceNumber ?? "",
      client: item.client ?? "",
      transactionType: item.transactionType ?? "",
      taxType: item.taxType ?? "",
      taxAmt: item.taxAmt ?? "",
      grossPremium: item.grossPremium ?? "",
      invDate: fmt(item.invDate),
      effectiveDate: fmt(item.effectiveDate),
      expirationDate: fmt(item.expirationDate),
    }))
  }, [matchedItems])

  const columns = useMemo(
    () => [
      { field: "policyNumber", headerName: "Policy #", flex: 1, minWidth: 120 },
      { field: "invoiceNumber", headerName: "Invoice #", flex: 1, minWidth: 100 },
      { field: "client", headerName: "Client", flex: 1, minWidth: 140 },
      { field: "transactionType", headerName: "Transaction Type", flex: 1, minWidth: 120 },
      { field: "taxType", headerName: "Tax Type", flex: 1, minWidth: 120 },
      { field: "taxAmt", headerName: "Tax Amt", width: 90 },
      { field: "grossPremium", headerName: "Gross Premium", width: 110 },
      { field: "invDate", headerName: "Inv Date", width: 100 },
      { field: "effectiveDate", headerName: "Effective Date", width: 110 },
      { field: "expirationDate", headerName: "Expiration Date", width: 110 },
    ],
    []
  )

  const selectedCount = Array.isArray(selectionModel) ? selectionModel.length : 0

  const handleRestoreSelected = async () => {
    if (selectedCount === 0) return
    const ids = rows
      .filter((r) => selectionModel.includes(r.id))
      .map((r) => r._customerPolicyId)
    const uniqueIds = Array.from(new Set(ids))
    setActionLoading(true)
    try {
      await onRestoreSelected(uniqueIds)
      onClose()
    } finally {
      setActionLoading(false)
    }
  }

  const handleRestoreAll = async () => {
    setActionLoading(true)
    try {
      await onRestoreAll()
      onClose()
    } finally {
      setActionLoading(false)
    }
  }

  const handleDontRestore = () => {
    onDontRestore()
    onClose()
  }

  const disableActions = loading || actionLoading

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Transactions in Removed Queue</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {matchedItems.length} of {totalImportCount} imported transaction(s) are in the
          Filing/Recon Removed queue. Select which ones to restore to Outstanding queues, then
          choose an action below.
        </Typography>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectionModel}
            onSelectionModelChange={(model) => setSelectionModel(model)}
            pageSize={PAGE_SIZE}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            disableColumnMenu={false}
            loading={loading}
            getRowId={(row) => row.id}
            hideFooterSelectedRowCount={false}
            sx={{
              "& .MuiTablePagination-toolbar": {
                minHeight: 44,
                alignItems: "center",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                marginTop: 0,
                marginBottom: 0,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiTablePagination-select": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleDontRestore} disabled={disableActions} color="inherit">
          Don&apos;t restore
        </Button>
        <Button
          onClick={handleRestoreSelected}
          disabled={disableActions || selectedCount === 0}
          variant="outlined"
        >
          Restore selected ({selectedCount})
        </Button>
        <Button
          onClick={handleRestoreAll}
          disabled={disableActions}
          variant="contained"
        >
          Restore all ({matchedCustomerPolicyIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  )
}
